import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { FormRenderProps } from 'react-final-form';
import equal from 'fast-deep-equal';
import { connect, useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';

import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { FiltersModal } from 'CorePackage/FiltersModalModule/components/FiltersModal';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { ISelectInputFieldProps, SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import {
  knmFilterFormBooleanSelectOptions,
  knmFilterFormSignedSelectOptions,
} from 'KnmListPackage/KnmListPanelModule/constants/knmListFilterOptions';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { ISetFilterCurrentParams } from 'CorePackage/FiltersModalModule/interfaces/ISetFilterCurrentParams';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { getUserProfileIsTest, getUserRoleIsProsecutor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IKnmStatusSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmStatusSelectOption';
import { ICountrySelectOption } from 'CorePackage/CatalogsModule/interfaces/ICountrySelectOption';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import {
  makeGetCatalogSelectOptions,
  getCatalogCountriesSelectOptions,
  getCatalogKnmYearsSelectOptions,
  getUserKnoSelectOptions,
  getCatalogsTerritorialUnitsSelectOptions,
  getCatalogDecisionStatusesSelectOptions,
  getUserProsecutorSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IYearSelectOption } from 'CorePackage/MainModule/interfaces/IYearSelectOption';
import { IGenericReasonSelectOption } from 'CorePackage/CatalogsModule/interfaces/IGenericReasonSelectOption';
import { IDictionaryTargetEntitySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryTargetEntitySelectOption';
import { IDictionarySubjectTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectTypeSelectOption';
import { IDictionarySubjectKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectKindSelectOption';
import { IDictionarySubjectSubKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectSubKindSelectOption';
import { IDictionaryRiskCategorySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryRiskCategorySelectOption';
import { IDictionaryDangerClassSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryDangerClassSelectOption';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IFilterResult } from 'APIPackage/MainModule/interfaces/IFilterResult';
import {
  getDictionariesKnoOrganizationsSelectOptions,
  makeGetDictionariesTargetEntitySelectOptions,
  makeGetDictionariesSubjectTypesMultiSelectOptions,
  makeGetDictionariesSubjectKindsMultiSelectOptions,
  makeGetDictionariesSubjectSubKindsMultiSelectOptions,
  makeGetDictionariesSupervisionTypesExtendedSelectOptions,
  getDictionariesRiskCategoriesData,
  getDictionariesDecisionKindsAllSelectOptions,
} from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { decisionListGetData } from 'DecisionListPackage/MainModule/actions/decisionListActions';
import { IDecisionKind } from 'APIPackage/DictionariesModule/interfaces/IDecisionKind';

import { convertFilterDataToControlOrganizationsIds } from '../common/convertFilterDataToControlOrganizationsIds';
import { convertFilterDataToSupervisionTypesIds } from '../common/convertFilterDataToSupervisionTypesIds';
import { initialDecisionListFilter } from '../constants/initialDecisionListFilter';
import {
  IDecisionListFilterSetCurrentParams,
  decisionListFilterCreate,
  decisionListFilterGetData,
  decisionListFilterInit,
  decisionListFilterRemove,
  decisionListFilterSetCurrent,
  decisionListFilterSetCurrentControlOrganizations,
  decisionListFilterSetCurrentSupervisionTypes,
  decisionListFilterSetMeta,
  decisionListFilterSetSwitcher,
  decisionListFilterUpdate,
} from '../actions/decisionListFilterActions';
import { IDecisionListFiltersForm } from '../interfaces/IDecisionListFiltersForm';
import { IDecisionListFilterExtraField } from '../interfaces/IDecisionListFilterExtraField';
import { IDecisionListFilter } from '../interfaces/IDecisionListFilter';
import { mapFilterAdditionalFields } from '../common/mapFilterAdditionalFields';
import {
  initialDecisionListAdditionalFields as initialDecisionListAdditionalFieldsConstant,
  isTestAdditionalField,
} from '../constants/initialDecisionListAdditionalFields';
import { IDecisionListFilterWithMeta } from '../interfaces/IDecisionListFilterWithMeta';
import {
  getDecisionListCurrentControlOrganizations,
  getDecisionListCurrentSupervisionTypes,
  getDecisionListFilterCurrentFilter,
  getDecisionListFilterCurrentId,
  getDecisionListFilterFilters,
} from '../selectors/decisionListFilterSelectors';
import { createDictionaryRiskCategorySelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionaryRiskCategorySelectOption';
import { createDictionaryDangerClassSelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionaryDangerClassSelectOption';
import { erknmPublishedStatusesSelectOptions } from '../../../CorePackage/MainModule/constants/erknmPublishedStatuses';
import { ITerritorialUnitSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/ITerritorialUnitSelectOption';

const mapFilter = (filter: IDecisionListFilter, filterName: string): IDecisionListFilter => {
  return {
    ...filter,
    name: filterName,
  };
};

interface IDecisionListFiltersModalProps {
  visible: boolean;
  onClose: () => void;
}

interface IDecisionListFiltersModalStoreProps {
  yearsSelectOptions: IYearSelectOption[];
  userKnoOrganisationsSelectOptions: IUserOrganisationSelectOption[] | undefined;
  roleProsecutor: boolean;
  filters: IDecisionListFilterWithMeta[];
  currentFilter: IDecisionListFilter | undefined;
  activeFilterId: number | undefined;
  decisionStatusesSelectOptions: IKnmStatusSelectOption[];
  countriesSelectOptions: ICountrySelectOption[];
  reasonsSelectOptions: IGenericReasonSelectOption[];
  targetEntitiesSelectOptions: IDictionaryTargetEntitySelectOption[] | undefined;
  subjectTypesSelectOptions: IDictionarySubjectTypeSelectOption[] | undefined;
  subjectKindsSelectOptions: IDictionarySubjectKindSelectOption[] | undefined;
  subjectSubKindsSelectOptions: IDictionarySubjectSubKindSelectOption[] | undefined;
  riskCategoriesSelectOptions: IDictionaryRiskCategorySelectOption[] | undefined;
  dangerClassesSelectOptions: IDictionaryDangerClassSelectOption[] | undefined;
  currentYearSelectOption: IYearSelectOption | undefined;
  erknmSupervisionTypesSelectOptions: IDictionarySupervisionTypeSelectOption[] | undefined;
  kindsSelectOptions: IDictionarySelectOption<IDecisionKind>[] | undefined;
  erknmOrganisationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
  territorialUnitsSelectOptions: ITerritorialUnitSelectOption[];
  userProsecutorOrganisationsSelectOptions: IUserOrganisationSelectOption[] | undefined;
  isUserTest: boolean;
}

const DecisionListFiltersModalWithoutStore = ({
  visible,
  onClose,
  activeFilterId,
  currentFilter,
  filters,
  currentYearSelectOption,
  decisionStatusesSelectOptions,
  countriesSelectOptions,
  reasonsSelectOptions,
  targetEntitiesSelectOptions,
  subjectTypesSelectOptions,
  subjectKindsSelectOptions,
  subjectSubKindsSelectOptions,
  riskCategoriesSelectOptions,
  dangerClassesSelectOptions,
  roleProsecutor,
  userKnoOrganisationsSelectOptions,
  yearsSelectOptions,
  erknmSupervisionTypesSelectOptions,
  erknmOrganisationsSelectOptions,
  kindsSelectOptions,
  territorialUnitsSelectOptions,
  userProsecutorOrganisationsSelectOptions,
  isUserTest,
}: IDecisionListFiltersModalProps & IDecisionListFiltersModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  useUnmount(() => {
    dispatch(decisionListFilterSetMeta([]));
  });

  const getDecisionListData = useCallback(() => dispatch(decisionListGetData()), [dispatch]);
  const getDecisionListFilterData = useCallback((id: IFilterResult['id']) => dispatch(decisionListFilterGetData(id)), [
    dispatch,
  ]);
  const setDecisionListFilterCurrent = useCallback(
    (params: IDecisionListFilterSetCurrentParams) => dispatch(decisionListFilterSetCurrent(params)),
    [dispatch],
  );
  const setDecisionListFilterSwitcher = useCallback(
    (value: boolean) => dispatch(decisionListFilterSetSwitcher(value)),
    [dispatch],
  );
  const createDecisionListFilter = useCallback(
    (data: IDecisionListFilter) => dispatch(decisionListFilterCreate(data)),
    [dispatch],
  );
  const updateDecisionListFilter = useCallback(
    (id: IFilterResult['id'], data: IDecisionListFilter) => dispatch(decisionListFilterUpdate(id, data)),
    [dispatch],
  );
  const removeDecisionListFilter = useCallback((id: IFilterResult['id']) => dispatch(decisionListFilterRemove(id)), [
    dispatch,
  ]);

  const setCurrentControlOrganizations = useCallback(
    (ids: string[] | undefined) => dispatch(decisionListFilterSetCurrentControlOrganizations(ids || [])),
    [dispatch],
  );

  const setCurrentSupervisionTypes = useCallback(
    (ids: string[] | undefined) => dispatch(decisionListFilterSetCurrentSupervisionTypes(ids || [])),
    [dispatch],
  );

  const initialDecisionListAdditionalFields = useMemo(() => {
    const result = [...initialDecisionListAdditionalFieldsConstant];

    if (isUserTest && !initialDecisionListAdditionalFieldsConstant.find(i => i.id === isTestAdditionalField.id)) {
      result.push(isTestAdditionalField);
    }

    return result;
  }, []);

  const mapper = useMemo(() => mapFilterAdditionalFields(isUserTest), [isUserTest]);

  const defaultKnoOrganisationSelectOption = useMemo(
    () =>
      erknmOrganisationsSelectOptions?.find(
        item =>
          item.id.toString() ===
          userKnoOrganisationsSelectOptions
            ?.find(userKnoOrganisationsSelectOption => userKnoOrganisationsSelectOption.byDefault)
            ?.id.toString(),
      ),
    [erknmOrganisationsSelectOptions, userKnoOrganisationsSelectOptions],
  );

  const defaultTerritorialUnitSelectOption = useMemo(
    () => territorialUnitsSelectOptions.find(selectOption => selectOption.isDefault),
    [territorialUnitsSelectOptions],
  );

  const memoizedInitialDecisionListFilter: IDecisionListFilter = useMemo(
    () => ({
      name: 'Новый фильтр',
      years: currentYearSelectOption ? [currentYearSelectOption] : [],
      ...(defaultTerritorialUnitSelectOption && { domains: [defaultTerritorialUnitSelectOption] }),
      includeDomainChild: false,
      includeChildProsecutorOrg: true,
      ignoreKno: false,
      includeChildKno: false,
      ...(defaultKnoOrganisationSelectOption && { controllingOrganizations: [defaultKnoOrganisationSelectOption] }),
      ...(kindsSelectOptions && kindsSelectOptions.length && { kinds: kindsSelectOptions }),
    }),
    [
      currentYearSelectOption,
      defaultKnoOrganisationSelectOption,
      kindsSelectOptions,
      defaultTerritorialUnitSelectOption,
    ],
  );

  const [formValues, setFormValues] = useState<IDecisionListFiltersForm>(initialDecisionListFilter);

  useEffect(() => {
    dispatch(decisionListFilterInit());
  }, [dispatch]);

  const memoizedFilterSelectFields: Omit<IDecisionListFilterExtraField, 'isSelected' | 'name'>[] = useMemo(
    () => [
      {
        id: 'supervisionType',
        options: erknmSupervisionTypesSelectOptions,
      },
      {
        id: 'deletedOnly',
        options: knmFilterFormBooleanSelectOptions,
      },
      {
        id: 'deletedInclude',
        options: knmFilterFormBooleanSelectOptions,
      },
      {
        id: 'statuses',
        options: decisionStatusesSelectOptions,
      },
      {
        id: 'subjectCountries',
        options: countriesSelectOptions,
      },
      {
        id: 'reasons',
        options: reasonsSelectOptions,
      },
      {
        id: 'subjectTypes',
        options: targetEntitiesSelectOptions,
      },
      {
        id: 'objectKinds',
        options: subjectKindsSelectOptions,
      },
      {
        id: 'objectSubKinds',
        options: subjectSubKindsSelectOptions,
      },
      {
        id: 'objectRiskCategory',
        options: riskCategoriesSelectOptions,
      },
      {
        id: 'objectDangerClass',
        options: dangerClassesSelectOptions,
      },
      {
        id: 'prosecutorOrganizations',
        options: userProsecutorOrganisationsSelectOptions,
      },
      {
        id: 'signed',
        options: knmFilterFormSignedSelectOptions,
      },
      {
        id: 'pubStatuses',
        options: erknmPublishedStatusesSelectOptions,
      },
      {
        id: 'objectTypes',
        options: subjectTypesSelectOptions,
      },
      {
        id: 'isTest',
        options: knmFilterFormBooleanSelectOptions,
      },
    ],
    [
      erknmSupervisionTypesSelectOptions,
      decisionStatusesSelectOptions,
      countriesSelectOptions,
      reasonsSelectOptions,
      targetEntitiesSelectOptions,
      subjectTypesSelectOptions,
      subjectKindsSelectOptions,
      subjectSubKindsSelectOptions,
      riskCategoriesSelectOptions,
      dangerClassesSelectOptions,
    ],
  );

  const [additionalFields, setAdditionalFields] = useState(initialDecisionListAdditionalFields);

  const onTemplateCreate = useCallback(() => {
    const ids = convertFilterDataToControlOrganizationsIds(
      erknmOrganisationsSelectOptions,
      memoizedInitialDecisionListFilter?.controllingOrganizations || [],
    );

    setCurrentControlOrganizations(ids || []);

    const newData = {
      ...initialDecisionListFilter,
      ...memoizedInitialDecisionListFilter,
    };

    setFormValues(newData);
    const filter = mapFilter(newData, 'Новый фильтр');
    const filterData: ISetFilterCurrentParams<IDecisionListFilter> = {
      current: { ...filter, isTemplateFirstCreate: true },
      currentId: undefined,
    };

    setDecisionListFilterCurrent(filterData);
    setAdditionalFields(initialDecisionListAdditionalFields);
  }, [
    setCurrentControlOrganizations,
    setCurrentSupervisionTypes,
    erknmOrganisationsSelectOptions,
    initialDecisionListAdditionalFields,
    memoizedInitialDecisionListFilter,
    setDecisionListFilterCurrent,
  ]);

  useEffect(() => {
    const newData = {
      ...initialDecisionListFilter,
      ...memoizedInitialDecisionListFilter,
      isTemplateFirstCreate: true,
    };

    if (!equal(formValues, newData) && activeFilterId === undefined && formValues.isTemplateFirstCreate) {
      onTemplateCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilterId, memoizedInitialDecisionListFilter]);

  const removeAdditionalField = (name: IDecisionListFilterExtraField['id']) => {
    setAdditionalFields(prev =>
      prev.map(f => {
        if (f.id === name) {
          return {
            ...f,
            isSelected: false,
          };
        }

        return f;
      }),
    );
  };

  function renderFormContent({ values, form }: FormRenderProps<IDecisionListFiltersForm>) {
    const makeRemoveFilterHandler = (name: IDecisionListFilterExtraField['id']) => () => {
      removeAdditionalField(name);
      form.change(name);
    };

    function handleChangeControllingOrganizations(value: IDictionarySelectOption<IKnoOrganizationDictionary>[]) {
      const ids = convertFilterDataToControlOrganizationsIds(erknmOrganisationsSelectOptions, value);

      dispatch(decisionListFilterSetCurrentControlOrganizations(ids || []));
    }

    function handleChangeSupervisionTypes(value: IDictionarySupervisionTypeSelectOption) {
      const ids = convertFilterDataToSupervisionTypesIds(erknmSupervisionTypesSelectOptions, [value]);

      dispatch(decisionListFilterSetCurrentSupervisionTypes(ids || []));
    }

    function isSelectInputFieldMultiClearableEqual(
      a: ISelectOption[] | null | undefined,
      b: ISelectOption[] | null | undefined,
    ) {
      const newA = a?.length === 0 ? undefined : a;
      const newB = b?.length === 0 ? undefined : b;

      return equal(newA, newB);
    }

    function validateControllingOrganizationsSelect(value: ISelectInputFieldProps['value']) {
      if (form.getState().values.ignoreKno) {
        return undefined;
      }

      return makeRequired('Поле не может быть пустым')(value);
    }

    function checkSameOptions(option: ISelectOption, selectValue: ISelectOption[]) {
      return selectValue.some(item => equal(option, item));
    }

    const primaryContent = (
      <>
        <Row>
          <FieldBlock label="Год решения (на основе даты решения)" labelFontSize="small">
            <SelectInputField
              id="years"
              name="years"
              options={yearsSelectOptions}
              isSearchable
              isMulti
              isClearable={false}
              isEqual={equal}
              validate={makeRequired('Поле не может быть пустым')}
            />
          </FieldBlock>
        </Row>

        <Row>
          <FieldBlock label="Территориальная единица" labelFontSize="small">
            <Row>
              <FieldBlock>
                <SelectInputField
                  id="domains"
                  name="domains"
                  options={territorialUnitsSelectOptions}
                  isSearchable
                  isMulti
                  isEqual={isSelectInputFieldMultiClearableEqual}
                  validate={makeRequired('Поле не может быть пустым')}
                />
              </FieldBlock>
            </Row>
            <CheckboxField
              id="includeDomainChild"
              name="includeDomainChild"
              label="Включая подчиненные организации"
              theme="system"
              type="checkbox"
            />
          </FieldBlock>
        </Row>

        <Row border="bottom">
          <FieldBlock label="Вид контрольного (надзорного) мероприятия, специального режима" labelFontSize="small">
            <SelectInputField
              id="kinds"
              name="kinds"
              options={kindsSelectOptions}
              isMulti
              isClearable={false}
              isEqual={equal}
              validate={makeRequired('Поле не может быть пустым')}
            />
          </FieldBlock>
        </Row>
        <FieldBlock label="Наименование органа контроля (надзора)" labelFontSize="small">
          <Row>
            <FieldBlock>
              <SelectInputField
                id="controllingOrganizations"
                name="controllingOrganizations"
                options={erknmOrganisationsSelectOptions}
                disabled={values.ignoreKno}
                isMulti
                isSearchable
                isEqual={isSelectInputFieldMultiClearableEqual}
                isOptionSelected={checkSameOptions}
                validate={validateControllingOrganizationsSelect}
                onChange={handleChangeControllingOrganizations}
              />
            </FieldBlock>
          </Row>
          <Row>
            <CheckboxField
              id="includeChildKno"
              name="includeChildKno"
              label="Включая подчиненные организации"
              theme="system"
              type="checkbox"
            />
          </Row>
          {roleProsecutor && (
            <CheckboxField
              id="ignoreKno"
              name="ignoreKno"
              label="Не учитывать орган контроля"
              theme="system"
              type="checkbox"
            />
          )}
        </FieldBlock>
      </>
    );

    const { controllingOrganizations, supervisionType } = form.getState().values;
    const areKnoAndSupervisionSpecified = controllingOrganizations?.length && supervisionType;

    const secondaryContent = (
      <>
        {additionalFields
          .filter(f => f.isSelected)
          .map((field, index) => (
            <Row
              key={field.id}
              border={index < additionalFields.filter(f => f.isSelected).length - 1 ? 'bottom' : undefined}
            >
              <FieldBlock
                label={field.name}
                labelFontSize="small"
                type="flex-label"
                button={<CloseButton onClick={makeRemoveFilterHandler(field.id)} />}
              >
                {field.type === 'checkbox' && (
                  <CheckboxField id={field.id} name={field.id} theme="system" type="checkbox" />
                )}
                {field.type === 'text' && (
                  <TextInputField name={field.id} type="text" validate={makeRequired('Поле не может быть пустым')} />
                )}
                {(field.id === 'objectTypes' || field.id === 'objectKinds' || field.id === 'objectSubKinds') &&
                  !areKnoAndSupervisionSpecified && <label>Выберите КНО и вид контроля</label>}
                {field.type === 'select' && (
                  <SelectInputField
                    id={field.id}
                    name={field.id}
                    options={memoizedFilterSelectFields.find(item => item.id === field.id)?.options}
                    isSearchable={field.isSearchable}
                    isMulti={field.isMulti}
                    isClearable={field.isClearable || false}
                    isEqual={field.isMulti && field.isClearable ? isSelectInputFieldMultiClearableEqual : equal}
                    validate={makeRequired('Поле не может быть пустым')}
                    onChange={field.id === 'supervisionType' ? handleChangeSupervisionTypes : undefined}
                    getOptionValue={v => v.value.dictVersionId || v.value}
                  />
                )}
              </FieldBlock>
            </Row>
          ))}
      </>
    );

    return {
      primaryContent,
      secondaryContent,
    };
  }

  return (
    <FiltersModal<IDecisionListFiltersForm, IDecisionListFilter>
      visible={visible}
      initialFormValues={formValues}
      additionalFields={additionalFields}
      filters={filters}
      currentFilter={currentFilter}
      activeFilterId={activeFilterId}
      renderFormContent={renderFormContent}
      mapFilter={mapFilter}
      mapFilterAdditionalFields={mapper}
      onClose={onClose}
      onTemplateCreate={onTemplateCreate}
      setFormValues={setFormValues}
      setAdditionalFields={setAdditionalFields}
      getData={getDecisionListData}
      getFilterData={getDecisionListFilterData}
      setFilterCurrent={setDecisionListFilterCurrent}
      setFilterSwitcher={setDecisionListFilterSwitcher}
      createFilter={createDecisionListFilter}
      updateFilter={updateDecisionListFilter}
      removeFilter={removeDecisionListFilter}
      excludedCounterFields={['includeChildProsecutorOrg']}
    />
  );
};

const mapStateToProps = (state: IRootState): IDecisionListFiltersModalStoreProps => {
  const decisionStatusesSelectOptions = getCatalogDecisionStatusesSelectOptions(state);
  const countriesSelectOptions = getCatalogCountriesSelectOptions(state);
  const knmYearsSelectOptions: IYearSelectOption[] = getCatalogKnmYearsSelectOptions(state);

  const currentYear = new Date().getFullYear();
  const nextYear = String(currentYear + 1);
  const currentYearSelectOption = knmYearsSelectOptions.find(
    selectOption => selectOption.value === String(currentYear),
  );

  const nextYearSelectOption = knmYearsSelectOptions.find(selectOption => selectOption.value === nextYear);
  const yearsSelectOptions = nextYearSelectOption
    ? knmYearsSelectOptions
    : [...knmYearsSelectOptions, { id: nextYear, label: nextYear, value: nextYear }];

  const controllingOrganizations = getDecisionListCurrentControlOrganizations(state);
  const supervisionTypes = getDecisionListCurrentSupervisionTypes(state);

  const getDictionariesSubjectTypesMultiSelectOptions = makeGetDictionariesSubjectTypesMultiSelectOptions();
  const getDictionariesSubjectKindsMultiSelectOptions = makeGetDictionariesSubjectKindsMultiSelectOptions();
  const getDictionariesSubjectSubKindsMultiSelectOptions = makeGetDictionariesSubjectSubKindsMultiSelectOptions();
  const areKnoAndSupervisionSpecified = controllingOrganizations.length && supervisionTypes.length;
  const subjectTypesSelectOptions = areKnoAndSupervisionSpecified
    ? getDictionariesSubjectTypesMultiSelectOptions(state, controllingOrganizations, supervisionTypes)
    : [];
  const subjectKindsSelectOptions = areKnoAndSupervisionSpecified
    ? getDictionariesSubjectKindsMultiSelectOptions(state, controllingOrganizations, supervisionTypes)
    : [];
  const subjectSubKindsSelectOptions = areKnoAndSupervisionSpecified
    ? getDictionariesSubjectSubKindsMultiSelectOptions(state, controllingOrganizations, supervisionTypes)
    : [];

  const getDictionariesSupervisionTypesSelectOptions = makeGetDictionariesSupervisionTypesExtendedSelectOptions();
  const getDictionariesGenericReasonsSelectOptions = makeGetCatalogSelectOptions(
    CommonCatalogsNamesEnum.DECISION_GENERIC_REASONS,
  );
  const getDictionariesTargetEntitySelectOptions = makeGetDictionariesTargetEntitySelectOptions();

  const riskAndDangerCategories = getDictionariesRiskCategoriesData(
    state,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
  );
  const riskCategoriesSelectOptions = riskAndDangerCategories?.values
    .filter(item => !item.value.code.startsWith('0'))
    .map(item => createDictionaryRiskCategorySelectOption(riskAndDangerCategories?.idDictionary, item));
  const dangerClassesSelectOptions = riskAndDangerCategories?.values
    .filter(item => item.value.code.startsWith('0'))
    .map(item => createDictionaryDangerClassSelectOption(riskAndDangerCategories?.idDictionary, item));

  return {
    activeFilterId: getDecisionListFilterCurrentId(state),
    currentFilter: getDecisionListFilterCurrentFilter(state),
    filters: getDecisionListFilterFilters(state),
    decisionStatusesSelectOptions,
    countriesSelectOptions,
    reasonsSelectOptions: getDictionariesGenericReasonsSelectOptions(state),
    targetEntitiesSelectOptions: getDictionariesTargetEntitySelectOptions(state, undefined)?.filter(
      targetEntityOption => targetEntityOption.code !== null,
    ),
    subjectTypesSelectOptions,
    subjectKindsSelectOptions,
    subjectSubKindsSelectOptions,
    riskCategoriesSelectOptions,
    dangerClassesSelectOptions,
    roleProsecutor: getUserRoleIsProsecutor(state),
    userKnoOrganisationsSelectOptions: getUserKnoSelectOptions(state),
    yearsSelectOptions,
    currentYearSelectOption,
    userProsecutorOrganisationsSelectOptions: getUserProsecutorSelectOptions(state),
    erknmOrganisationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
    erknmSupervisionTypesSelectOptions: getDictionariesSupervisionTypesSelectOptions(
      state,
      controllingOrganizations || [],
      false,
      false,
    ),
    kindsSelectOptions: getDictionariesDecisionKindsAllSelectOptions(state),
    territorialUnitsSelectOptions: getCatalogsTerritorialUnitsSelectOptions(state),
    isUserTest: getUserProfileIsTest(state),
  };
};

export const DecisionListFiltersModal = connect(mapStateToProps)(DecisionListFiltersModalWithoutStore);
